<script lang="ts">
import type { StudyMarketOutlookSlideProps } from '$lib/components/study/slide-layout-types'
import StudyBlueCalloutCard from '../cards/StudyBlueCalloutCard.svelte'
import StudySlideLineChartCard from '../cards/StudySlideLineChartCard.svelte'
import StudySlideStatTileCard from '../cards/StudySlideStatTileCard.svelte'
import StudyTimelineListCard from '../cards/StudyTimelineListCard.svelte'
import StudySlide from '../StudySlide.svelte'
import StudySlideContent from '../StudySlideContent.svelte'

/** Timeline + quarterly spend line + signal callout + compact stat tiles (`s8`). */
let props: StudyMarketOutlookSlideProps = $props()

const eyebrowStyle = props.eyebrowStyle ?? 'margin-bottom:0.35rem;'
const headlineStyle = props.headlineStyle ?? 'margin-bottom:0.72rem;'
const tcStyle = props.tcStyle ?? 'gap:1rem;align-items:start;'
const lineY = props.lineYTickFormat ?? ((v: number) => '$' + v + 'B')
const lineTip = props.lineTooltipFormat ?? ((v: number) => ` $${v.toFixed(1)}B`)
const borderC = props.lineBorderColor ?? '#11a0a5'
const fillC = props.lineFillColor ?? 'rgba(17,160,165,.12)'
const tilesGrid = props.tilesGridStyle ?? 'display:grid;grid-template-columns:1fr 1fr;gap:.5rem;'
const tileVariant = props.statTileVariant ?? 'compact'
const rightCol = props.rightColumnStyle ?? 'display:flex;flex-direction:column;gap:.5rem;'

// Normalize projectedYears / projectedValues to the shorter of the two lengths so they
// stay in sync (avoids `Array(-1)` from `length - 1` when spendLineValues is empty,
// avoids out-of-range null padding when projectedValues outpaces projectedYears, etc.).
const rawProjYears = props.projectedYears ?? []
const rawProjValues = props.projectedValues ?? []
const projLen = Math.min(rawProjYears.length, rawProjValues.length)
const projYears = rawProjYears.slice(0, projLen)
const projValues = rawProjValues.slice(0, projLen)
const hasProjection = projLen > 0 && props.spendLineValues.length > 0
const allLabels = hasProjection ? [...props.quarterLabels, ...projYears] : props.quarterLabels
const lineDatasets = hasProjection
  ? [
      {
        data: [...props.spendLineValues, ...Array(projLen).fill(null)] as (number | null)[],
        borderColor: borderC,
        backgroundColor: fillC,
      },
      {
        // Connector point at the last historical index, then the projected series.
        // Math.max guards against Array(-1) if spendLineValues is somehow empty
        // (defensive — `hasProjection` already requires length > 0).
        data: [
          ...Array(Math.max(0, props.spendLineValues.length - 1)).fill(null),
          props.spendLineValues[props.spendLineValues.length - 1],
          ...projValues,
        ] as (number | null)[],
        borderColor: borderC,
        backgroundColor: 'rgba(17,160,165,.04)',
        borderDash: [5, 4],
      },
    ]
  : [
      {
        data: props.spendLineValues,
        borderColor: borderC,
        backgroundColor: fillC,
      },
    ]
</script>

<StudySlide
  id={props.id}
  active={props.active}
  extraBottom={props.extraBottom}
  background={props.background}
  slideClass={props.slideClass}
>
  <StudySlideContent
    maxWidth={props.maxWidth ?? '980px'}
    contentClass={props.contentClass}
    style={props.contentStyle}
  >
    <div class="ey" style={eyebrowStyle}>{@html props.eyebrow}</div>
    <div class="hl" style={headlineStyle}>{@html props.headline}</div>
    <div class="tc" style={tcStyle}>
      <StudyTimelineListCard items={props.timelineItems} />
      <div style={rightCol}>
        <StudySlideLineChartCard
          active={props.active ?? false}
          title={props.spendLineTitle}
          subtitle={props.spendLineSubtitle}
          labels={allLabels}
          datasets={lineDatasets}
          height={props.lineChartHeight ?? '152px'}
          yTickFormat={lineY}
          tooltipFormat={(v) => lineTip(v)}
          maxTicksLimit={props.lineMaxTicksLimit ?? 7}
        />
        <StudyBlueCalloutCard
          bodyHtml={props.signalCalloutHtml}
          cardStyle={props.signalCalloutCardStyle ?? 'padding:.65rem .78rem;'}
          paragraphStyle={props.signalCalloutParagraphStyle ??
            'margin:0;font-size:.82rem;line-height:1.42;'}
        />
        <div style={tilesGrid}>
          {#each props.statTiles as t}
            <StudySlideStatTileCard
              variant={tileVariant}
              value={t.value}
              label={t.label}
              valueColor={t.valueColor}
            />
          {/each}
        </div>
      </div>
    </div>
  </StudySlideContent>
</StudySlide>
